import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Play, Loader2, AlertCircle } from 'lucide-react';
import { laborScheduleService } from '../services/laborScheduleService';
import { formatCalendarOption } from '../utils/laborCalendarUtils';
import { LaborCalendarModalShell } from './LaborCalendarModalShell';

export function LaborGenerateDaysModal({
  isOpen,
  onClose,
  factoryId,
  factoryCode,
  defaultYear,
  onSuccess,
}) {
  const queryClient = useQueryClient();
  const currentYear = new Date().getFullYear();
  const [calendarId, setCalendarId] = useState('');
  const [generateYear, setGenerateYear] = useState(defaultYear);
  const [error, setError] = useState(null);

  const {
    data: calendars = [],
    isLoading: loadingCalendars,
    isError: listError,
    error: listErrorDetail,
  } = useQuery({
    queryKey: ['labor-calendars', factoryId],
    queryFn: () => laborScheduleService.listCalendarsByFactory(factoryId),
    enabled: isOpen && Boolean(factoryId),
  });

  useEffect(() => {
    if (isOpen) {
      setGenerateYear(defaultYear);
      setError(null);
    }
  }, [isOpen, defaultYear]);

  useEffect(() => {
    if (!isOpen || calendars.length === 0) return;
    const match = calendars.find((c) => Number(c.year) === Number(defaultYear));
    const pick = match || calendars[0];
    setCalendarId(String(pick.id));
  }, [isOpen, calendars, defaultYear]);

  const mutation = useMutation({
    mutationFn: () => {
      const calId = Number(calendarId);
      if (!calId) {
        throw new Error('Selecciona un calendario de la lista.');
      }
      return laborScheduleService.generateCalendarDays(calId, generateYear);
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['labor-calendar-schedule'] });
      await queryClient.invalidateQueries({ queryKey: ['labor-calendar-pattern-list'] });
      onSuccess?.(data);
      onClose();
    },
    onError: (err) => {
      setError(err?.message || 'No se pudieron generar los días');
    },
  });

  const footer = (
    <>
      <button
        type="button"
        onClick={onClose}
        disabled={mutation.isPending}
        className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 text-sm"
      >
        Cancelar
      </button>
      <button
        type="button"
        onClick={() => {
          setError(null);
          mutation.mutate();
        }}
        disabled={mutation.isPending || !calendarId || loadingCalendars}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium disabled:opacity-50"
      >
        {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        Generar días
      </button>
    </>
  );

  return (
    <LaborCalendarModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Generar días del calendario"
      icon={Play}
      footer={footer}
    >
      {error && (
        <div className="mb-4 p-3 rounded-lg border border-red-800 bg-red-900/20 flex gap-2 text-red-300 text-sm">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <p className="text-sm text-gray-400 mb-4">
        Fábrica <span className="text-white font-medium">{factoryCode}</span>. Elige un
        calendario existente de esta fábrica y el año de días a generar.
      </p>

      {listError && (
        <div className="mb-4 p-3 rounded-lg border border-red-800 bg-red-900/20 text-red-300 text-sm">
          {listErrorDetail?.message || 'No se pudo cargar la lista de calendarios'}
        </div>
      )}

      <label htmlFor="labor-gen-cal" className="block text-sm text-gray-300 mb-2">
        Calendario <span className="text-red-400">*</span>
      </label>
      {loadingCalendars ? (
        <p className="text-gray-500 text-sm mb-4 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Cargando calendarios…
        </p>
      ) : calendars.length === 0 ? (
        <p className="text-amber-400 text-sm mb-4 bg-amber-900/20 border border-amber-700/50 rounded-lg p-3">
          No hay calendarios para esta fábrica. Créalo primero con el botón &quot;Crear
          calendario&quot;.
        </p>
      ) : (
        <select
          id="labor-gen-cal"
          value={calendarId}
          onChange={(e) => setCalendarId(e.target.value)}
          disabled={mutation.isPending}
          className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm mb-4"
        >
          {calendars.map((cal) => (
            <option key={cal.id} value={cal.id}>
              {formatCalendarOption(cal)}
            </option>
          ))}
        </select>
      )}

      <label htmlFor="labor-gen-year" className="block text-sm text-gray-300 mb-2">
        Año a generar <span className="text-red-400">*</span>
      </label>
      <select
        id="labor-gen-year"
        value={generateYear}
        onChange={(e) => setGenerateYear(Number(e.target.value))}
        disabled={mutation.isPending}
        className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
      >
        {[currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </LaborCalendarModalShell>
  );
}
